// @flow

import React, { type Node } from 'react';
import styled  from 'styled-components';

import { getFirstName } from '@tectonics/commons/get-first-name';
import { CapitalizedSpan } from '@tectonics/components/capitalized';

import { IfFeature } from '@tectonics/prs/feature';
import { useAllFeatures } from '@tectonics/prs/payment-spec';
import { getSafeCustomerName, useIncomingPayment, getSafeDueDate } from '@tectonics/prs/incoming-payment';

import { Separator } from '../../../atoms';
import { MerchantInfo, MerchantLogo } from '../../../organisms/merchant-info';

import { BoletoTip } from './boleto-tip';
import { MailWarning } from './mail-warning';
import { BoletoNumber } from './boleto-number';
import { BoletoBarcode } from './boleto-barcode';
import { ShowBoletoButton } from './show-boleto-button';
import { ShowBoletoPdfButton } from './show-boleto-pdf-button';
import { DueDateWarning } from '../../../organisms/due-date-warning';
import { DisplayDueDate } from '../../../organisms/display-due-date';
import { DisplayTotalAmount } from '../../../organisms/display-total-amount';
import { getWeekDay } from '@tectonics/prs/week-day';

export function BoletoPage(): Node {
  const incomingPayment = useIncomingPayment();
  const features = useAllFeatures();

  if(incomingPayment.status !== 'PE')
    throw new Error(`Boleto: invalid payment status ${incomingPayment.status}, it should be PE`);

  const firstName = getFirstName(getSafeCustomerName(incomingPayment)).toLowerCase();

  return (
    <Content>
      <MerchantLogo height="100px" />

      <BoletoTitle>
        <CapitalizedSpan>{firstName}</CapitalizedSpan>, seu boleto foi gerado. <br />Pague agora!
      </BoletoTitle>

      <Body>
        <PaymentInfoWrapper>

          <IfFeature allFeatures={features} wanted="display_boleto_total_value">
            <DisplayTotalAmount label="Valor do boleto" />
          </IfFeature>

          <IfFeature allFeatures={features} wanted="display_boleto_due_date">
            <DisplayDueDate label="Vencimento" />
          </IfFeature>

          <DueDateWarning message={`Pague até ${getWeekDay(getSafeDueDate(incomingPayment), dueDateWeekDay)}`}/>

        </PaymentInfoWrapper>

        <Hr />

        <IfFeature allFeatures={features} wanted="display_boleto_email_warning">
          <MailWarning/>
        </IfFeature>

        <IfFeature allFeatures={features} wanted="display_boleto_number">
          <BoletoNumber />
        </IfFeature>

        <IfFeature allFeatures={features} wanted="display_boleto_barcode">
          <BoletoBarcode />
        </IfFeature>

        <LinkButtonsWrapper>

          <IfFeature allFeatures={features} wanted="action_boleto_copy_code">
            <ShowBoletoButton />
          </IfFeature>

          <IfFeature allFeatures={features} wanted="action_boleto_save_pdf">
            <ShowBoletoPdfButton />
          </IfFeature>

        </LinkButtonsWrapper>

        <IfFeature allFeatures={features} wanted="display_boleto_tips">
          <BoletoTip />
        </IfFeature>

        <Separator direction="vertical" size={4} />

        <MerchantInfo />

      </Body>
    </Content>
  );
}

const BoletoTitle = styled.p`
  margin: 0;
  color: ${props => props.theme.colors.black};
  font-family: ${props => props.theme.fontFamily.display};
  font-weight: 500;
  font-size: 17px;
  line-height: 1.5;
  text-align: center;
  padding: 16px 0;
  background-color: ${props => props.theme.colors.gray(96)};

  @media (min-width: 840px) {
    display: none;
  }
`;

const Body = styled.div`
  line-height: 1.5;
`;

const Hr = styled.hr`
  display: block; height: 1px;
  border: 0;
  border-top: 1px solid rgb(244, 246, 248);
  margin: 1.6em 0; padding: 0;
`;

const Content = styled.section`
  display: flex;
  flex-direction: column;
  text-align: center;
  padding: 0 16px 32px 16px;
  font-family: ${props => props.theme.fontFamily.sansSerif};

  > * {
    width: 100%;
    margin: 16px auto 6px;
  }

  @media (min-width: 840px) {
    height: auto;
    padding: 0 32px 16px 32px;
  }
`;

const PaymentInfoWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const LinkButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const dueDateWeekDay = [
  'segunda-feira', // No due date in Sunday, so go to Monday
  'segunda-feira',
  'terça-feira',
  'quarta-feira',
  'quinta-feira',
  'sexta-feira',
  'segunda-feira', // No due date in Saturday, so go to Monday
];
